import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createBookingMutation,
  recordBookingPaymentMutation,
  voidBookingPaymentMutation,
  isProblem,
  listBookingsInfiniteQueryKey,
  mayHaveActed,
  resendGuestLinkMutation,
  runBookingActionMutation,
  runBookingInterventionMutation,
  updateBookingMutation,
  type CreateBooking,
  type BookingActionRequest,
  type BookingInterventionRequest,
  type UpdateBooking,
  type RecordBookingPayment,
} from '@/shared/api'
import { bookingDetailQuery } from './queries'

/** Одна попытка создать бронь: тело плюс ключ, который обязан пережить повторы. */
export type NewBooking = {
  body: CreateBooking
  /**
   * Мнётся ОДИН раз — на первое нажатие «Создать бронь» — и приходит с КАЖДЫМ повтором.
   * Правило целиком — в `shared/api/idempotency.ts`.
   */
  idempotencyKey: string
}

/** Создание брони в полёте — по этому ключу его видит модалка, которую нельзя закрыть посреди запроса. */
export const CREATE_BOOKING_KEY = ['bookings', 'create'] as const

/**
 * Создание брони.
 *
 * ⚠️ КЛЮЧ ИДЕМПОТЕНТНОСТИ ПРИХОДИТ СНАРУЖИ. Он принадлежит не монтированию (склеил бы две разные
 * брони подряд) и не вызову (`randomUUID()` внутри `mutate()` тихо уничтожает всю защиту), а
 * ДЕЙСТВИЮ — одному нажатию вместе со всеми повторами. Цена ошибки ровно та, от которой ключ и
 * защищает: двойной клик или повтор после «сеть икнула» дают ВТОРУЮ бронь живому гостю. Сервер
 * помочь не может — два разных ключа он обязан считать разными действиями, а клиент не отличает
 * «не получил» от «получил, создал, а ответ потерялся». Владеть ключом может только форма
 * (`features/booking-create`), которая знает, что такое «то же самое действие».
 *
 * ⚠️ ЛОКАЛЬНЫЙ `onError` НЕ ЗАТИРАЕТ `MutationCache` — тот вызывается независимо и ПЕРВЫМ; затеняется
 * только `defaultOptions.mutations.onError` (неглубокий спред опций), ради чего телеметрию и
 * перенесли на `MutationCache`. Разница не косметическая: оптимистичным мутациям локальный `onError`
 * НУЖЕН для отката, и ложная приписка отговорила бы от него следующего исполнителя. Здесь он есть —
 * перечитать список, если бронь могла лечь (`mayHaveActed`), — и общий тост от него не молчит.
 *
 * Свежую бронь кладём в кеш карточки: `POST` отдаёт `201 BookingDetail` — ровно то, что читает
 * карточка, — и без записи `?booking=bk_x` пошёл бы на сервер за тем, что уже в руках.
 */
export function useCreateBooking() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...createBookingMutation(),
    mutationKey: CREATE_BOOKING_KEY,
    onSuccess: (detail) => {
      queryClient.setQueryData(bookingDetailQuery(detail.id).queryKey, detail)
      void queryClient.invalidateQueries({ queryKey: listBookingsInfiniteQueryKey() })
    },
    // Исход мог состояться — список, которым его проверяют, перечитываем: опроса у него нет.
    onError: (error) => {
      if (mayHaveActed(error)) void queryClient.invalidateQueries({ queryKey: listBookingsInfiniteQueryKey() })
    },
  })

  const vars = ({ body, idempotencyKey }: NewBooking) => ({
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  })

  return {
    ...mutation,
    mutate: (booking: NewBooking) => mutation.mutate(vars(booking)),
    mutateAsync: (booking: NewBooking) => mutation.mutateAsync(vars(booking)),
  }
}

/*
 * ⚠️ НИЖЕ ОБРАБОТЧИКИ БЕРУТ БРОНЬ ИЗ `variables.path`, А НЕ ИЗ ПАРАМЕТРА ХУКА. `useMutation` отдаёт
 * свежие опции ЕЩЁ ЛЕТЯЩЕЙ мутации (`MutationObserver.setOptions`, ветка `pending`), и у экрана,
 * пережившего смену брони, ответ брони А лёг бы из замыкания в карточку Б. Модалка брони секции и
 * окна пересоздаёт ключом брони, но правило на это не опирается: ключ — свойство экрана, а не хука.
 * Заслон — `mutations.test.tsx`.
 */

/**
 * Действие из `actionsRequired` карточки («Подтвердить платёж» и т.п.).
 *
 * `Idempotency-Key` у ручки нет, и это не упущение: действие закрывает КОНКРЕТНЫЙ пункт, повтору
 * закрывать уже нечего — сервер отвечает «такого действия у брони нет», а не делает работу дважды.
 * Наружу гостю оно ничего не пишет.
 *
 * Ответ — свежая `BookingDetail`, кладём в кеш, чтобы карточка обновилась в тот же кадр. Список
 * инвалидируем: действие могло сменить статус, и строка должна уйти из фильтра.
 */
export function useRunBookingAction(bookingId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...runBookingActionMutation(),
    onSuccess: (detail, { path }) => {
      queryClient.setQueryData(bookingDetailQuery(path.bookingId).queryKey, detail)
      void queryClient.invalidateQueries({ queryKey: listBookingsInfiniteQueryKey() })
    },
  })

  return {
    ...mutation,
    // Причина обязательна у каждого действия (3.2) — поэтому она в СИГНАТУРЕ, а не в необязательном
    // поле: вызвать действие «без почему» не должно быть возможно даже случайно.
    mutate: (request: RunBookingActionRequest) =>
      mutation.mutate({ path: { bookingId, actionKey: request.actionKey }, body: request.body }),
    mutateAsync: (request: RunBookingActionRequest) =>
      mutation.mutateAsync({ path: { bookingId, actionKey: request.actionKey }, body: request.body }),
  }
}

export type RunBookingActionRequest = {
  actionKey: string
  body: BookingActionRequest
}

/**
 * «Вмешаться в бронь» (3.2B).
 *
 * ⚠️ ОТКАЗ ЗДЕСЬ — УСПЕШНЫЙ ОТВЕТ, А НЕ ОШИБКА: перевод на новую версию система вправе отклонить и
 * объяснить причины (`applied: false` + `refusedReasons`) кодом 200. Разложи мы это по ветке ошибки,
 * менеджер получил бы красный тост «не удалось», хотя удалось ровно то, что должно было: система
 * ответила. Поэтому `onSuccess` не считает ответ применённой правкой — разбирается вызывающий.
 *
 * Бронь в ответе свежая в ОБОИХ случаях (при отказе она не изменилась, но могла уехать с прошлого
 * чтения), поэтому кладём её в кеш всегда.
 */
export function useRunBookingIntervention(bookingId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...runBookingInterventionMutation(),
    onSuccess: (result, { path }) => {
      queryClient.setQueryData(bookingDetailQuery(path.bookingId).queryKey, result.booking)
      // Список — ТОЛЬКО когда правка применилась: у отказа бронь не изменилась, а инвалидация
      // перечитала бы все загруженные страницы бесконечного списка ради ничего.
      if (result.applied) {
        void queryClient.invalidateQueries({ queryKey: listBookingsInfiniteQueryKey() })
      }
    },
  })

  const vars = (request: RunBookingInterventionRequest) => ({
    path: { bookingId, interventionKey: request.key },
    body: request.body,
  })

  return {
    ...mutation,
    // Сужаем ОБА входа: сырой `mutate` принял бы чужой `bookingId` в пути, и вмешательство молча
    // применилось бы к ДРУГОЙ брони.
    mutate: (request: RunBookingInterventionRequest) => mutation.mutate(vars(request)),
    mutateAsync: (request: RunBookingInterventionRequest) => mutation.mutateAsync(vars(request)),
  }
}

export type RunBookingInterventionRequest = {
  key: string
  body: BookingInterventionRequest
}

/**
 * Переотправка гостю ссылки на бота (3.5).
 *
 * ⚠️ В ОТВЕТЕ НЕТ НИ КОДА, НИ АДРЕСА — только куда ушло и каких ботов покрывает. Ссылку отправляет
 * сам сервер; показать её менеджеру значило бы её перехватить: она одноразовая и привязывает к
 * брони тот чат, который её открыл.
 *
 * ⚠️ НИЧЕГО НЕ ИНВАЛИДИРУЕМ, и это не забывчивость. Бронь от переотправки не меняется, `chats`
 * перечисляет мессенджеры, где гость бота ОТКРЫВАЛ, а отправка открыть не заставляет. Меняется
 * только переписка, и её лента рядом перечитывается сама. Инвалидация же снесла бы подвал вместе с
 * подтверждением «ссылка ушла»: поповер показывает скелетон на любом перезапросе.
 */
export function useResendGuestLink(bookingId: string) {
  const mutation = useMutation(resendGuestLinkMutation())

  return {
    ...mutation,
    // Путь сужаем здесь: сырой `mutate` принял бы чужой `bookingId`, и ссылка ушла бы гостю ДРУГОЙ
    // брони — молча, потому что ответ выглядел бы точно так же.
    mutate: () => mutation.mutate({ path: { bookingId } }),
    mutateAsync: () => mutation.mutateAsync({ path: { bookingId } }),
  }
}

/**
 * Правка брони — три поля, которые домен у брони правит: число гостей и два примечания.
 *
 * ⚠️ ВЕРСИЮ ПРИНОСИТ ВЫЗЫВАЮЩИЙ, И ЭТО НЕ ЦЕРЕМОНИЯ. `If-Match` обязан нести ТУ версию, которую
 * человек видел, когда решал править. Прочитать её здесь из кеша было бы удобнее и неверно: между
 * открытием формы и «Сохранить» соседняя вкладка или поллинг могли положить туда свежую бронь, и мы
 * отправили бы «поверх того, что там сейчас» — ровно то, от чего `If-Match` защищает, только с
 * видимостью защиты.
 *
 * ⚠️ 409 — НЕ ОШИБКА СЕТИ, а «бронь успели поменять»: надо показать, что правка не применилась, и
 * дать перечитать. Разбор — у формы, которая его ловит.
 *
 * Ответ — свежая `BookingDetail` с УЖЕ увеличенной версией; кладём в кеш, чтобы следующая правка
 * ушла с правильным `If-Match`.
 */
export function useUpdateBooking(bookingId: string, subject: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...updateBookingMutation(),
    /*
     * ⚠️ ОБЩИЙ ТОСТ МОЛЧИТ, ПОКА ОКНО НА МЕСТЕ: ОНО ОБЪЯСНЯЕТ ОТКАЗ САМО. Иначе 409 даёт ДВА ответа об
     * одном событии: точную серверную фразу в окне и «Что-то сломалось — обновите страницу» в углу.
     * Второй советует ПРОТИВОПОЛОЖНОЕ — перезагрузка конфликт версий не лечит, а `classifyError` не
     * отличает 409 от собственного бага приложения. Ушло окно — тост скажет сам, назвав `subject`.
     *
     * ⚠️ Двойной ответ поймал СКРИНШОТ, а не зонд: зонд читал плашку в окне и тоста в углу не видел.
     *
     * ⚠️ Флаг принадлежит тому, кто ОТРИСОВЫВАЕТ отказ. Единственный потребитель — окно правки, и
     * оно показывает любую ошибку. Появится второй, который этого не делает, — флаг отдать параметром.
     */
    meta: { handledInPlace: true, subject },
    /*
     * ⚠️ СПИСОК НЕ ИНВАЛИДИРУЕМ: `BookingRow` несёт статус, имя, объект, даты, ночи, источник и
     * сумму — и НИ ОДНОГО из трёх правимых полей.
     */
    onSuccess: (detail, { path }) => {
      queryClient.setQueryData(bookingDetailQuery(path.bookingId).queryKey, detail)
    },
    /*
     * ⚠️ НА КОНФЛИКТЕ ПЕРЕЧИТЫВАЕМ КАРТОЧКУ САМИ, иначе совет окна «откройте бронь заново» — ложь: у
     * карточки `staleTime` полминуты, и закрыв-открыв её человек получил бы ТУ ЖЕ версию из кеша, а
     * следующая попытка снова упала бы в 409. Ручка обязана чинить то, о чём сообщает.
     */
    onError: (error, { path }) => {
      if (isProblem(error) && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: bookingDetailQuery(path.bookingId).queryKey })
      }
    },
  })

  const vars = ({ body, version }: BookingEdit) => ({
    path: { bookingId },
    body,
    headers: { 'If-Match': String(version) },
  })

  /*
   * Опции вызова ПРОПУСКАЕМ ДАЛЬШЕ, а не глотаем: закрыть окно после удачного сохранения — дело
   * окна, а не сущности. Проглоти мы второй аргумент, вызов `mutate(edit, { onSuccess })` молча
   * ничего бы не сделал. Соседние обёртки этого не делают только потому, что их потребителям не
   * понадобилось, — не потому, что так задумано.
   */
  type MutateOptions = Parameters<typeof mutation.mutate>[1]

  return {
    ...mutation,
    mutate: (edit: BookingEdit, options?: MutateOptions) => mutation.mutate(vars(edit), options),
    mutateAsync: (edit: BookingEdit, options?: MutateOptions) =>
      mutation.mutateAsync(vars(edit), options),
  }
}

/** Одна правка брони: что меняем и поверх какой версии. */
export type BookingEdit = {
  body: UpdateBooking
  /** `BookingDetail.version`, прочитанная ПЕРЕД правкой, — см. разбор у `useUpdateBooking`. */
  version: number
}

/**
 * Ручная запись платежа по брони.
 *
 * ⚠️ ОТВЕТ — ЛЕНТА ЦЕЛИКОМ СО СВОДОМ, А НЕ ДОБАВЛЕННАЯ СТРОКА: после записи меняется весь свод —
 * оплаченное, залог, проценты. Дособрать его на клиенте значило бы держать вторую реализацию их
 * арифметики, ту самую, что однажды разойдётся с первой и покажет неверные деньги. В кеш кладём,
 * подменяя ТОЛЬКО секцию платежей: остальное от записи не меняется.
 *
 * ⚠️ `Idempotency-Key` ПРИХОДИТ СНАРУЖИ — он принадлежит ДЕЙСТВИЮ, а не вызову функции. Смяв его
 * здесь, мы получили бы повтор после «сервер не ответил», записывающий ВТОРОЙ платёж живыми деньгами.
 *
 * Общий тост ГЛУШИМ, пока окно на месте: отказ показывает оно само строкой `role="alert"`. Правило в
 * проекте одно — кто отрисовал ошибку у себя, тот и снимает общий тост (`meta.handledInPlace`); иначе
 * поверх точной фразы окна всплывает «Что-то сломалось», и человек читает две версии одного отказа.
 * Ушло окно — тост скажет сам, назвав `subject`, а после обрыва — что исход неизвестен: записан ли
 * платёж, не знает никто, а повтор из нового окна — новый ключ, то есть второй платёж.
 */
export function useRecordBookingPayment(bookingId: string, subject: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...recordBookingPaymentMutation(),
    meta: { handledInPlace: true, subject },
    onSuccess: (payments, { path }) => {
      queryClient.setQueryData(bookingDetailQuery(path.bookingId).queryKey, (previous) =>
        previous ? { ...previous, payments } : previous,
      )
    },
    // Платёж мог лечь — ленту, которой это проверяют, перечитываем: опроса у карточки нет.
    onError: (error, { path }) => {
      if (mayHaveActed(error)) {
        void queryClient.invalidateQueries({ queryKey: bookingDetailQuery(path.bookingId).queryKey })
      }
    },
  })

  const vars = ({ body, idempotencyKey }: NewBookingPayment) => ({
    path: { bookingId },
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  })

  type MutateOptions = Parameters<typeof mutation.mutate>[1]

  return {
    ...mutation,
    mutate: (payment: NewBookingPayment, options?: MutateOptions) =>
      mutation.mutate(vars(payment), options),
    mutateAsync: (payment: NewBookingPayment, options?: MutateOptions) =>
      mutation.mutateAsync(vars(payment), options),
  }
}

/** Одна попытка записать платёж: тело плюс ключ, который обязан пережить повторы. */
export type NewBookingPayment = {
  body: RecordBookingPayment
  idempotencyKey: string
}

/**
 * Отмена ошибочной ручной записи.
 *
 * ⚠️ КЛЮЧА ИДЕМПОТЕНТНОСТИ ЗДЕСЬ НЕТ, И ЭТО НЕ ПРОПУСК: отмена идемпотентна по построению — она
 * адресует КОНКРЕТНУЮ запись и переводит её в одно и то же состояние. Повтор отменит уже отменённое,
 * в отличие от записи, где повтор создал бы вторую строку живыми деньгами.
 *
 * Отказ на строке площадки (422) до сервера доходить не должен: кнопка рисуется только там, где
 * сервер разрешил (`canVoid`). Дойдёт — окно покажет его словами сервера.
 */
export function useVoidBookingPayment(bookingId: string, subject: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    ...voidBookingPaymentMutation(),
    meta: { handledInPlace: true, subject },
    onSuccess: (payments, { path }) => {
      queryClient.setQueryData(bookingDetailQuery(path.bookingId).queryKey, (previous) =>
        previous ? { ...previous, payments } : previous,
      )
    },
  })

  return {
    ...mutation,
    // Путь сужаем здесь: сырой `mutate` принял бы чужой `bookingId`, и отменилась бы запись ДРУГОЙ
    // брони — молча, потому что ответ выглядел бы точно так же.
    mutate: (paymentId: string) => mutation.mutate({ path: { bookingId, paymentId } }),
    mutateAsync: (paymentId: string) => mutation.mutateAsync({ path: { bookingId, paymentId } }),
  }
}
